import React, { ReactNode, useCallback, useEffect } from 'react';
//import actions
import {
    changeLayout,
    changeSidebarTheme,
    changeLayoutMode,
    changeLayoutWidth,
    changeLayoutPosition,
    changeTopbarTheme,
    changeLeftsidebarSizeType,
    changeLeftsidebarViewType,
    changeSidebarImageType,
    changeBodyImageType
} from "slices/thunk";
//redux
import { useSelector, useDispatch } from "react-redux";
// import TopBar from './TopBar';

import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { changeLayoutTheme } from 'slices/layouts/thunk';
import withRouter from 'Common/withRouter';
import { createSelector } from 'reselect';
import ProductAssistantWidget from 'features/productAssistant/ProductAssistantWidget';
import { useBrandMetadata } from 'features/branding/useBrandMetadata';
import { useLocation } from 'react-router-dom';
import api from 'Common/api';
import { BillingRecoveryModalWithStripe, BillingAccessState } from 'components/BillingRecoveryModal';
import { AlertCircle } from 'lucide-react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { BillingAccessProvider } from 'components/BillingAccessContext';
import { getAuthPayload, isContractorUser } from 'Common/permissions';
interface LayoutProps {
    children: ReactNode;
}
const Layout = (props: LayoutProps) => {

    useBrandMetadata();
    const location = useLocation();
    const isChatRoute = ["/apps-chat", "/messages", "/chat-room", "/logan-ai"]
        .some((route) => location.pathname === route || location.pathname.startsWith(`${route}/`));
    const isProfileSettingsRoute = location.pathname === "/pages-profile-settings";
    const [billingAccess, setBillingAccess] = useState<BillingAccessState | null>(null);
    const [showBillingRecovery, setShowBillingRecovery] = useState(false);
    const isContractorBillingUser = isContractorUser(getAuthPayload().userType);

    const dispatch: any = useDispatch();
    const selectLayoutProperties = createSelector(
        (state: any) => state.Layout,
        (layout) => ({
          layoutType: layout.layoutType,
          layoutThemeType: layout.layoutThemeType,
          leftSidebarType: layout.leftSidebarType,
          layoutModeType: layout.layoutModeType,
          layoutWidthType: layout.layoutWidthType,
          layoutPositionType: layout.layoutPositionType,
          topbarThemeType: layout.topbarThemeType,
          leftsidbarSizeType: layout.leftsidbarSizeType,
          leftSidebarViewType: layout.leftSidebarViewType,
          leftSidebarImageType: layout.leftSidebarImageType,
          bodyImageType: layout.bodyImageType
        })
      );
      
      const {
        layoutType,
        layoutThemeType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        bodyImageType
      } = useSelector(selectLayoutProperties);
    /*
    layout settings
    */
    useEffect(() => {
        if (
            layoutType ||
            layoutThemeType ||
            leftSidebarType ||
            layoutModeType ||
            layoutWidthType ||
            layoutPositionType ||
            topbarThemeType ||
            leftsidbarSizeType ||
            leftSidebarViewType ||
            leftSidebarImageType ||
            bodyImageType
        ) {
            window.dispatchEvent(new Event('resize'));
            dispatch(changeLeftsidebarViewType(leftSidebarViewType));
            dispatch(changeLayoutTheme(layoutThemeType));
            dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
            dispatch(changeSidebarTheme(leftSidebarType));
            dispatch(changeLayoutMode(layoutModeType));
            dispatch(changeLayoutWidth(layoutWidthType));
            dispatch(changeLayoutPosition(layoutPositionType));
            dispatch(changeTopbarTheme(topbarThemeType));
            dispatch(changeLayout(layoutType));
            dispatch(changeSidebarImageType(leftSidebarImageType));
            dispatch(changeBodyImageType(bodyImageType));
        }
    }, [layoutType,
        layoutThemeType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        bodyImageType,
        dispatch]);

    useEffect(() => {
        if (layoutModeType !== "auto") return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const syncSystemTheme = () => dispatch(changeLayoutMode("auto"));
        mediaQuery.addEventListener("change", syncSystemTheme);

        return () => mediaQuery.removeEventListener("change", syncSystemTheme);
    }, [dispatch, layoutModeType]);

    useEffect(() => {
        if (!isContractorBillingUser) {
            setBillingAccess(null);
            setShowBillingRecovery(false);
            return;
        }

        let active = true;
        api.get('/stripe-sub/billing-access-state')
            .then((response: any) => {
                if (!active) return;
                const nextState = response?.data as BillingAccessState;
                setBillingAccess(nextState);
                setShowBillingRecovery(Boolean(nextState?.applies && nextState?.actionRequired));
            })
            .catch(() => {
                // Billing health must never make the authenticated shell unusable.
            });
        return () => { active = false; };
    }, [isContractorBillingUser]);

    const handleBillingRecovered = (nextState: BillingAccessState) => {
        setBillingAccess(nextState);
        setShowBillingRecovery(false);
    };
    const openPaymentRecovery = useCallback(() => setShowBillingRecovery(true), []);
    return (
        <BillingAccessProvider value={{ state: billingAccess, openPaymentRecovery, completePaymentRecovery: handleBillingRecovered }}>
            <div id="layout-wrapper">
                <Sidebar layoutType={layoutType} />
                <Header />
                <div className={`main-content${isProfileSettingsRoute ? " main-content--without-footer" : ""}${billingAccess?.actionRequired && !showBillingRecovery ? " has-subscription-alert" : ""}`}>
                    {billingAccess?.actionRequired && !showBillingRecovery ? (
                        <div className={`subscription-access-banner${billingAccess.accessLevel === 'suspended' ? ' is-suspended' : ''}`} role="alert">
                            <div className="subscription-access-banner__icon">
                                <AlertCircle size={20} aria-hidden="true" />
                            </div>
                            <div className="subscription-access-banner__content">
                                <strong>{billingAccess.statusReason === 'provider_subscription_missing' ? 'Subscription is not active' : 'Subscription payment needs attention'}</strong>
                                <span>
                                    {billingAccess.statusReason === 'provider_subscription_missing'
                                        ? 'Choose a plan to restore premium actions.'
                                        : billingAccess.accessLevel === 'suspended'
                                        ? 'Premium actions are paused until the outstanding payment is completed.'
                                        : 'Update your payment method to avoid service interruption.'}
                                </span>
                            </div>
                            <div className="subscription-access-banner__actions">
                                <Button size="sm" variant={billingAccess.accessLevel === 'suspended' ? 'danger' : 'warning'} onClick={() => setShowBillingRecovery(true)}>{billingAccess.statusReason === 'provider_subscription_missing' ? 'View options' : 'Fix payment'}</Button>
                                <Button as={Link as any} to="/invoices-list" size="sm" variant="outline-secondary">View invoices</Button>
                            </div>
                        </div>
                    ) : null}
                    {props.children}
                    {!isChatRoute && !isProfileSettingsRoute && <Footer />}
                    {!isChatRoute && <ProductAssistantWidget />}
               

                </div>
                {billingAccess?.actionRequired && showBillingRecovery ? (
                    <BillingRecoveryModalWithStripe
                        state={billingAccess}
                        onDismiss={() => setShowBillingRecovery(false)}
                        onRecovered={handleBillingRecovered}
                    />
                ) : null}
            </div>
        </BillingAccessProvider>
    );
}

export default withRouter(Layout);
